import { useState } from "react";
import { Avatar, Box, IconButton, Typography } from "@mui/material";
import {
  communityDetailsContainerStyle,
  communityDetailsSubContainerStyle,
  communityDetailsHeadingStyle,
  communityDetailsTextStyle,
  clientDetailsWrapper,
  communityDetailsLogoTextWrapper,
  clientCommunityDetailsContainerStyle,
  timeLocationRowStyle,
  timeLocationKeyStyle,
  timeLocationValueStyle,
  totalRowStyle,
  totalRowTextStyle,
  VATTextStyle,
} from "../styles/styles";
import { formatDateToDDMYY } from "../../../Utils/dateFormat";
import { getLocationContent } from "../../../Utils/getLocationContent";
import { formatPrice } from "../../../Helpers/formating";
import CustomTooltip from "../../../Components/CustomTooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useSnackbar } from "../../../Components/SnackBar";
import AddLinkModal from "../../../Components/Modals/AddLinkModal";
import { Trans, useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import { getCurrencySymbol } from "../../../Utils/CountriesData/getCountriesData";

const ClientCommunityDetails = (props) => {
  const { jobData, viewType, editMode = false } = props;
  const {
    client_community_details,
    time_location,
    contact_info,
    order_summary,
  } = jobData;
  const { member, community } = client_community_details;
  const showSnackbar = useSnackbar();
  const [openAddLinkModal, setOpenAddLinkModal] = useState(false);
  const [savedLink, setSavedLink] = useState(jobData.online_link);

  const { t: ttable } = useTranslation("page-my-jobs", {
    keyPrefix: "table_data",
  });
  const { t } = useTranslation("view-job-modal");
  const { t: tHatavot } = useTranslation("hatavot");
  const theme = useTheme();

  const AddLinkClickHandler = () => {
    setOpenAddLinkModal(true);
  };

  const currencySymbol = getCurrencySymbol(order_summary?.currency);

  const timeLocationArr = [
    {
      [t("Created On")]: time_location.created_at
        ? formatDateToDDMYY(time_location.created_at)
        : "-",
    },
    {
      [t("Due Date")]: time_location.due_date
        ? formatDateToDDMYY(time_location.due_date)
        : time_location.due_date_type === "WEEKENDS"
          ? t("weekends")
          : time_location.due_date_type === "FLEXIBLE"
            ? t("flexible")
            : "-",
    },
    {
      [t("Location")]: getLocationContent(
        time_location.locations,
        ttable,
        communityDetailsTextStyle,
        viewType !== "pending" && viewType !== "missed",
        viewType === "active" ? AddLinkClickHandler : undefined,
        viewType === "pending" || viewType === "missed",
        !!savedLink,
      ),
    },
    {
      [t("Time of Day")]: (() => {
        if (!time_location.time_of_day?.length) return "-";
        const order = ["MORNING", "MIDDAY", "AFTERNOON", "EVENING", "AFTER_HOURS"];
        const sorted = [...time_location.time_of_day].sort(
          (a, b) => order.indexOf(a) - order.indexOf(b)
        );
        return (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {sorted.map((val, idx) => (
              <span key={idx}>
                {/* Time ranges sit in <bdi> in the copy so they don't flip in RTL. */}
                <Trans
                  t={t}
                  i18nKey={`parts_of_day.${val}`}
                  defaults={val}
                  components={{ bdi: <bdi /> }}
                />
              </span>
            ))}
          </Box>
        );
      })(),
    },
  ];

  // Show the "Visit Price" row for a genuine visit order (marker) OR an order
  // with nothing on it yet (no services and no products) — a pure visit. A
  // product-only order (has products) uses the normal price row instead of a
  // null visit price. COMY1-292.
  const isVisitPrice =
    !!order_summary?.is_visit_order ||
    ((jobData.requested_services?.length ?? 0) === 0 &&
      (jobData.products?.length ?? 0) === 0);

  // Products attached to the service order (COMY1-292). `price` already
  // includes them, so the price row shows services ONLY and products get their
  // own line — otherwise the rows don't add up to the total, and on a visit
  // order the products were invisible entirely ("visit ₪3 ... total ₪5.1").
  const productsPrice = Number(order_summary?.products_price) || 0;
  const servicesPrice = (Number(order_summary?.price) || 0) - productsPrice;

  // "15% (−₪149.85)": the percentage alongside the amount it takes off. Both
  // values come from the API as-is; nothing is recalculated here.
  const percentWithAmount = (percent, amount) => (
    <bdi>
      {`${percent}%`}
      {amount ? ` (−${currencySymbol}${formatPrice(amount)})` : ""}
    </bdi>
  );

  const orderSummaryArr = [
    {
      [isVisitPrice
        ? t("Visit Price")
        : viewType !== "completed"
          ? t("price_estimated")
          : t("Price")]:
        order_summary?.visit_price || order_summary?.price
          ? `${currencySymbol}${formatPrice(
              isVisitPrice ? order_summary?.visit_price : servicesPrice,
            )}`
          : "-",
    },
    ...(productsPrice > 0
      ? [{ [t("products_heading")]: `${currencySymbol}${formatPrice(productsPrice)}` }]
      : []),
    {
      // A percentage while the order is services only — one rate, one base, so
      // it reads clearly. Once products are attached they carry their OWN
      // per-category rates, so no single percentage describes the deduction and
      // we show the combined amount instead (matching the products summary).
      [t("Community discount")]: productsPrice > 0
        ? order_summary?.community_discount
          ? `\u200E-${currencySymbol}${formatPrice(order_summary.community_discount)}`
          : "-"
        : order_summary?.community_discount_percent
          ? percentWithAmount(
              order_summary.community_discount_percent,
              order_summary?.community_discount,
            )
          : "-",
    },
    // \u05D4\u05D8\u05D1\u05D4 discount line \u2014 a deal the member claimed that actually changes the
    // price (percentage / fixed amount) lands here, so the supplier sees its
    // effect in the summary rather than only as informational text. Same wire
    // fields the product order uses. Non-monetary deals (a free gift) carry no
    // amount and stay in the claimed-deals section only (COMY1-359).
    ...(order_summary?.hatava_discount
      ? [{
          [tHatavot("job.discount_line")]: (
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "5px" }}>
              {`\u200E-${currencySymbol}${formatPrice(order_summary.hatava_discount)}`}
              {order_summary?.hatava_note ? (
                <CustomTooltip title={order_summary.hatava_note} tooltipSxStyles={{ paddingY: "6px" }}>
                  <Box component="span" sx={{ display: "inline-flex", cursor: "pointer" }}>
                    <InfoOutlinedIcon sx={{ fontSize: 15, color: theme.palette.customColors.textFaint }} />
                  </Box>
                </CustomTooltip>
              ) : null}
            </Box>
          ),
        }]
      : []),
    {
      // Shown as a percentage to match the products summary. Falls back to the
      // amount when no rate came back — the rate is the real per-deal one once
      // the order is paid, and a projection before that.
      //
      // Note the two percentages here sit on different bases: the community
      // discount is a share of the price, this fee a share of what's left after
      // that discount.
      [t("Keilot Fee")]:
        order_summary?.commy_fee_percent != null
          ? percentWithAmount(
              order_summary.commy_fee_percent,
              order_summary?.commy_fee,
            )
          : order_summary?.commy_fee
            ? `\u200E-${currencySymbol}${formatPrice(order_summary?.commy_fee)}`
            : "-",
    },
  ];

  return (
    <>
      <Box className="hide-scrollbar" sx={communityDetailsContainerStyle}>
        <Box
          sx={{
            ...communityDetailsSubContainerStyle,
            paddingTop: { xs: "0px", md: "24px" },
          }}
        >
          <Typography sx={communityDetailsHeadingStyle}>
            {t("client_community_details")}
          </Typography>

          <Box sx={clientCommunityDetailsContainerStyle}>
            <Box sx={clientDetailsWrapper}>
              <Box sx={communityDetailsLogoTextWrapper}>
                <Avatar
                  alt={
                    viewType !== "pending" && viewType !== "missed"
                      ? member.name
                      : ""
                  }
                  src={
                    viewType !== "pending" && viewType !== "missed"
                      ? member.image_url
                      : ""
                  }
                  sx={{ width: 38, height: 38 }}
                />
                <Typography sx={communityDetailsTextStyle}>
                  {viewType === "pending" || viewType === "missed"
                    ? t("member")
                    : member?.name || t("member")}
                </Typography>
              </Box>

              {viewType !== "pending" &&
              viewType !== "missed" &&
              (contact_info?.email || contact_info?.phone) ? (
                <Box sx={{ ml: "-8px" }}>
                  {contact_info?.email ? (
                    <CustomTooltip
                      title={t("copy_email")}
                      tooltipSxStyles={{ paddingY: "5px" }}
                    >
                      <IconButton
                        onClick={() => {
                          navigator.clipboard.writeText(contact_info?.email);
                          showSnackbar(t("email_copied_alert"), "success");
                        }}
                      >
                        <img src="/icons/mail-forward.svg" alt="mail" />
                      </IconButton>
                    </CustomTooltip>
                  ) : null}
                  {contact_info?.phone ? (
                    <CustomTooltip
                      title={t("copy_phone")}
                      tooltipSxStyles={{ paddingY: "5px" }}
                    >
                      <IconButton
                        onClick={() => {
                          navigator.clipboard.writeText(contact_info?.phone);
                          showSnackbar(t("phone_copied_alert"), "success");
                        }}
                      >
                        <img src="/icons/phone-outgoing.svg" alt="phone" />
                      </IconButton>
                    </CustomTooltip>
                  ) : null}
                  {contact_info?.phone ? (
                    <CustomTooltip
                      title={t("open_whatsapp")}
                      tooltipSxStyles={{ paddingY: "5px" }}
                    >
                      <IconButton
                        onClick={(e) => {
                          e.preventDefault();
                          window.open(
                            `https://api.whatsapp.com/send/?phone=${contact_info?.phone}&text=hello`,
                            "_blank",
                            "noopener,noreferrer",
                          );
                        }}
                      >
                        <img src="/icons/whatsapp-icon.svg" alt="whatsapp" />
                      </IconButton>
                    </CustomTooltip>
                  ) : null}
                </Box>
              ) : null}
            </Box>

            <Box sx={communityDetailsLogoTextWrapper}>
              <Avatar
                alt={community.name}
                src={community.logo_url}
                sx={{ width: 38, height: 38 }}
              />
              <Typography sx={communityDetailsTextStyle}>
                {community.name}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            ...communityDetailsSubContainerStyle,
            border: viewType === "missed" && "none",
          }}
        >
          <Typography sx={communityDetailsHeadingStyle}>
            {t("time_location")}
          </Typography>
          <Box sx={{ ...clientCommunityDetailsContainerStyle, gap: "12px" }}>
            {timeLocationArr.map((item, idx) => (
              <Box key={`row-${idx}`} sx={timeLocationRowStyle}>
                <Typography sx={timeLocationKeyStyle}>
                  {Object.keys(item)[0]}
                </Typography>
                <Typography sx={timeLocationValueStyle}>
                  {Object.values(item)[0]}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
        <Box
          sx={{
            ...communityDetailsSubContainerStyle,
            border: "none",
            paddingBottom: { xs: "0px", md: "24px" },
            display: editMode || viewType === "missed" ? "none" : "flex",
          }}
        >
          <Typography sx={communityDetailsHeadingStyle}>
            {t("order_summary")}
          </Typography>

          <Box sx={{ ...clientCommunityDetailsContainerStyle, gap: "13px" }}>
            {orderSummaryArr.map((item, idx) => (
              <Box key={`key-${idx}`} sx={timeLocationRowStyle}>
                <Typography sx={timeLocationKeyStyle}>
                  {Object.keys(item)[0]}
                </Typography>

                <Typography sx={communityDetailsTextStyle}>
                  {Object.values(item)[0]}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box sx={totalRowStyle}>
            <Typography sx={totalRowTextStyle}>
              {viewType === "completed" ? t("total_revenue") : t("total_estimated")}
              <Typography sx={VATTextStyle}>{t("VAT_included")}</Typography>
              {order_summary?.is_estimate && (
                <Typography sx={VATTextStyle}>{t("estimate_note")}</Typography>
              )}
            </Typography>

            <Typography sx={totalRowTextStyle}>
              {order_summary?.total
                ? currencySymbol + formatPrice(order_summary?.total)
                : "-"}
            </Typography>
          </Box>
        </Box>
      </Box>

      <AddLinkModal
        open={openAddLinkModal}
        onClose={() => setOpenAddLinkModal(false)}
        jobId={jobData?.job_id}
        existingLink={savedLink}
        onSave={(link) => setSavedLink(link)}
      />
    </>
  );
};

export default ClientCommunityDetails;
